import { defineStore } from "pinia";
import { ref } from "vue";
import { INSPECTION_LINKS_ENDPOINT } from "@/constants/Endpoints";
import axios from "axios";
import { LOGGER } from "@/util/logger";
import { clone } from "@/util/clone";
import { MODULE_TYPES } from "@/constants/InspectionConstants";
import { useInspectionStore } from "@/stores/inspection";
import { useConfigStore } from "@/stores/config";
import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { STRUCTURE_UNIT_TYPE } from "@/constants/Inspections";
import { filterStructureUnitsByLegacyType } from "@/composables/spanBySpan";

export const useNotesStore = defineStore("notes", () => {
  let pageData = ref({});
  const inspectionStore = useInspectionStore();
  const configStore = useConfigStore();

  const loadDataObject = async (moduleType, brkey, inspectionId, structure) => {
    if (moduleType === MODULE_TYPES.INSPECTION) {
      //if moduleType is inspection, load data from pouchDB
      convertToInspModuleObject(structure);
    } else if (moduleType === MODULE_TYPES.MANAGEMENT && brkey && inspectionId) {
      //if moduleType is management, load data from service call
      await getInspLinkDataObject(brkey, inspectionId);
    }
  };

  const getNotes = () => {
    return clone(pageData.value);
  };

  const saveData = async (moduleType, value, structure) => {
    let isSuccess = false;
    if (moduleType === MODULE_TYPES.INSPECTION) {
      convertToSaveInspModuleObject(value, structure);
      isSuccess = true;
    } else if (moduleType === MODULE_TYPES.MANAGEMENT) {
      isSuccess = await updateData(convertToSaveInspLinkObject(value));
    }
    return isSuccess;
  };

  const updateData = async (payload) => {
    let isSuccess = true;
    const url = INSPECTION_LINKS_ENDPOINT.UPDATE_NOTES;
    await axios
      .put(url, payload)
      .then((response) => {
        LOGGER.logEvent(LOGGER.EventAction.Update, LOGGER.EventCat.RequestResponse, "Update Notes", response);
        convertToInspLinkObject(response.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSuccess = false;
      });
    return isSuccess;
  };

  const convertToSaveInspModuleObject = (notesConversion, structure) => {
    structure.structureNotesId = notesConversion.structureNotesId;
    structure.notes = notesConversion.notes;
    structure.structureNoteType = notesConversion.structureNoteType;
    structure.inspectionComments = notesConversion.inspectionComments;
    structure.structureUnitInspComments = notesConversion.structureUnitInspComments;
  };

  const getInspLinkDataObject = async (brkey, inspectionId) => {
    pageData.value = {};
    let url = INSPECTION_LINKS_ENDPOINT.GET_NOTES.replace("{brkey}", brkey).replace("{inspectionId}", inspectionId);

    await axios
      .get(url)
      .then((response) => {
        convertToInspLinkObject(response.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
      });
  };

  const convertToInspModuleObject = (structure) => {
    pageData.value = {
      brkey: structure.brkey,
      structureNotesId: structure.structureNotesId,
      notes: structure.notes,
      inspectionComments: structure.inspectionComments ?? [],
      structureNoteType: structure.structureNoteType,
      structureUnits: inspectionStore.getFilteredStructureUnits(
        [
          STRUCTURE_UNIT_TYPE.PIER,
          STRUCTURE_UNIT_TYPE.ABUTMENT,
          STRUCTURE_UNIT_TYPE.APPROACH,
          STRUCTURE_UNIT_TYPE.MAIN,
          STRUCTURE_UNIT_TYPE.CULVERT,
        ],
        true
      ),
      structureUnitLocations: convertStructureUnitsToLocationsNotes(
        inspectionStore.getFilteredStructureUnits(
          [
            STRUCTURE_UNIT_TYPE.PIER,
            STRUCTURE_UNIT_TYPE.ABUTMENT,
            STRUCTURE_UNIT_TYPE.APPROACH,
            STRUCTURE_UNIT_TYPE.MAIN,
            STRUCTURE_UNIT_TYPE.CULVERT,
          ],
          true
        )
      ),
      structureUnitInspComments: structure.structureUnitInspComments ?? [],
    };
  };

  const convertToSaveInspLinkObject = (notesConversion) => {
    return {
      brkey: notesConversion.brkey,
      inspectionId: notesConversion.inspectionId,
      inspectionComments: notesConversion.inspectionComments,
      structureUnitInspComments: notesConversion.structureUnitInspComments,
      structureUnits: notesConversion.structureUnits,
    };
  };

  const convertStructureUnitsToLocationsNotes = (structureUnits) => {
    return structureUnits
      ?.map((b) => ({
        title: `${b?.strUnitLabel}-${b?.strUnitKey}`,
        value: b.structureUnitId.toString(),
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  };

  const convertToInspLinkObject = (response) => {
    pageData.value = {
      brkey: response.brkey,
      inspectionId: response.inspectionId,
      inspectionComments: response.inspectionComments,
      structureUnitInspComments: response.structureUnitInspComments,
      structureNoteType: response.structureNoteType,
      structureUnits: filterStructureUnitsByLegacyType(
        response.structureUnits,
        [
          STRUCTURE_UNIT_TYPE.PIER,
          STRUCTURE_UNIT_TYPE.ABUTMENT,
          STRUCTURE_UNIT_TYPE.APPROACH,
          STRUCTURE_UNIT_TYPE.MAIN,
          STRUCTURE_UNIT_TYPE.CULVERT,
        ],
        true
      ),
      structureUnitLocations: convertStructureUnitsToLocationsNotes(
        filterStructureUnitsByLegacyType(
          response.structureUnits,
          [
            STRUCTURE_UNIT_TYPE.PIER,
            STRUCTURE_UNIT_TYPE.ABUTMENT,
            STRUCTURE_UNIT_TYPE.APPROACH,
            STRUCTURE_UNIT_TYPE.MAIN,
            STRUCTURE_UNIT_TYPE.CULVERT,
          ],
          true
        )
      ),
    };
  };

  return {
    convertStructureUnitsToLocationsNotes,
    getNotes,
    saveData,
    loadDataObject,
    configStore,
    REFERENCE_TABLE,
    inspectionStore,
  };
});
